import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Alert from '../../../_shared/components/Alert';
import {
  FileUploadBrowse,
  FileUploadDragArea,
  FileUploadForm,
  FileUploadInput,
  FileUploadInputLabel,
  FileUploadInputLabelSpan
} from './Styles';
import {
  MaxFileSizeLabel,
  StyledCancelButton,
  StyledSubmitDocumentsButton
} from '../../Modal/PaymentsOnHoldUploadDocModal/Styles';
import PaymentsOnHoldService from '../../../services/PaymentsOnHoldService';
import FilePreview from '../FilePreview';
import ScaleLoader from '../../../_shared/components/ScaleLoader';
import PaymentsOnHoldUploadService from '../../../services/PaymentsOnHoldUploadService';
import TRToast from '../../../_shared/helpers/TRToast';
import PaymentsOnHoldContext from '../Context';
import { EtrUploadedDocumentType } from '../../../_shared/types/postSale';

const ETR_DOCUMENT_TYPE_MAP = {
  0: EtrUploadedDocumentType.TITLE,
  1: EtrUploadedDocumentType.LIEN
};

const FileUpload = ({
  trade,
  documentType,
  previouslyUploadedDocs,
  onClose
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadedEtrDocuments, setUploadedEtrDocuments] = useState([]);
  const [previouslyUploadedEtrDocs, setPreviouslyUploadedEtrDocs] = useState(
    []
  );
  const { onEtrSubmitDocuments } = useContext(PaymentsOnHoldContext);

  useEffect(() => {
    if (previouslyUploadedDocs) {
      setUploadedDocuments(previouslyUploadedDocs.presignedS3Uris);
    }

    if (trade?.isEtrTrade && trade?.uploadedDocuments?.length) {
      const uploadedDocumentsByType = trade?.uploadedDocuments?.filter(
        uploadedDocument =>
          uploadedDocument.documentType === ETR_DOCUMENT_TYPE_MAP[documentType]
      );

      setPreviouslyUploadedEtrDocs(uploadedDocumentsByType);
      setUploadedDocuments(
        uploadedDocumentsByType.map(uploadedDoc => uploadedDoc.preSignedUrl)
      );
    }
  }, [
    previouslyUploadedDocs,
    trade?.isEtrTrade,
    trade?.uploadedDocuments,
    documentType
  ]);

  const displayToast = (variant, msg) => {
    TRToast(
      <Alert variant={variant} messages={[<FormattedMessage id={msg} />]} />
    );
  };

  const removeDocument = s3Uri => {
    const array = [...uploadedDocuments];
    const index = uploadedDocuments.indexOf(s3Uri);
    array.splice(index, 1);
    setUploadedDocuments(array);
  };

  const removeEtrDocument = previewUrl => {
    setUploadedEtrDocuments(prevEtrDocuments =>
      prevEtrDocuments.filter(etrDoc => etrDoc.filePreviewUrl !== previewUrl)
    );
    removeDocument(previewUrl);
  };

  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleEtrDrop = async e => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (!files || !files.length) return;
    setUploading(true);
    setDragActive(false);
    try {
      const presignedUrlPromises = [];
      const uploadPromises = [];
      const filePreviewUrls = [];

      [...files].forEach(file => {
        filePreviewUrls.push(URL.createObjectURL(file));
        presignedUrlPromises.push(
          PaymentsOnHoldUploadService.generateEtrPresignedUrl(
            trade.purchaseId,
            ETR_DOCUMENT_TYPE_MAP[documentType],
            file
          )
        );
      });
      const presignedUrlResponses = await Promise.all(presignedUrlPromises);

      presignedUrlResponses.forEach((response, index) => {
        uploadPromises.push(
          PaymentsOnHoldUploadService.uploadFile(
            response.presignedUrl,
            files[index]
          )
        );
      });
      await Promise.all(uploadPromises);

      setUploadedDocuments(prevDocuments => [
        ...prevDocuments,
        ...filePreviewUrls
      ]);
      setUploadedEtrDocuments(prevEtrDocuments => [
        ...prevEtrDocuments,
        ...presignedUrlResponses.map((response, index) => ({
          ...response,
          filePreviewUrl: filePreviewUrls[index]
        }))
      ]);
    } catch (error) {
      displayToast('error', 'paymentsOnHold.uploadDocument.failed');
    }

    setUploading(false);
  };

  const handleDrop = async e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setUploading(true);
    const atLeastOneFileDropped =
      (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) ||
      (e.target && e.target.files && e.target.files[0]);
    if (!atLeastOneFileDropped) {
      setDragActive(true);
      setUploading(false);
      return;
    }
    // at least one file has been dropped/clicked so get
    // pre-signed upload url and attempt to upload to s3
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    const presignedUrls = await PaymentsOnHoldUploadService.generatePresignedUrls(
      trade.id,
      documentType,
      files
    );
    if (
      !presignedUrls ||
      !presignedUrls.presignedS3Uris ||
      presignedUrls.presignedS3Uris.length !== files.length
    ) {
      setUploading(false);
      displayToast('error', 'paymentsOnHold.uploadDocument.failed');
      return;
    }
    const presignedUrlUploads = [];
    for (let i = 0; i < presignedUrls.presignedS3Uris.length; i += 1) {
      presignedUrlUploads.push(
        PaymentsOnHoldUploadService.uploadFile(
          presignedUrls.presignedS3Uris[i],
          files[i]
        )
      );
    }
    const allFilesUploaded = Promise.all(presignedUrlUploads);
    const statusesUpdated = allFilesUploaded.then(
      () => {
        return PaymentsOnHoldUploadService.markDocumentUploadStatus(
          presignedUrls.documentIds,
          true
        );
      },
      () => {
        return PaymentsOnHoldUploadService.markDocumentUploadStatus(
          presignedUrls.documentIds,
          false
        );
      }
    );
    statusesUpdated
      .then(
        previewFileUrls => {
          if (
            previewFileUrls &&
            previewFileUrls.length === presignedUrls.documentIds.length
          ) {
            setUploadedDocuments(uploadedDocuments.concat(previewFileUrls));
          } else {
            displayToast('error', 'paymentsOnHold.uploadDocument.failed');
          }
        },
        () => {
          displayToast('error', 'paymentsOnHold.uploadDocument.failed');
        }
      )
      .finally(() => {
        setUploading(false);
      });
  };

  const handleSubmitDocumentsClick = () => {
    setSubmitting(true);
    PaymentsOnHoldService.submitSellerFundingDocs(
      trade.id,
      documentType,
      trade.vin
    )
      .then(
        () => {
          onClose();
          displayToast('success', 'paymentsOnHold.submitDocument.success');
        },
        () => {
          displayToast('error', 'paymentsOnHold.submitDocument.failed');
        }
      )
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleEtrSubmitDocumentsClick = async () => {
    setSubmitting(true);
    try {
      const submitEtrSellerFundingDocPromises = [];
      uploadedEtrDocuments.forEach(uploadedEtrDocument => {
        submitEtrSellerFundingDocPromises.push(
          PaymentsOnHoldService.submitEtrSellerFundingDoc(
            trade.purchaseId,
            ETR_DOCUMENT_TYPE_MAP[documentType],
            uploadedEtrDocument.key
          )
        );
      });
      await Promise.all(submitEtrSellerFundingDocPromises);
      onClose();
      onEtrSubmitDocuments();
      displayToast('success', 'paymentsOnHold.submitDocument.success');
    } catch {
      displayToast('error', 'paymentsOnHold.submitDocument.failed');
    }

    setSubmitting(false);
  };

  return (
    <FileUploadForm
      id="form-file-upload"
      data-testid="file-upload-form"
      onDragEnter={handleDrag}
      onSubmit={e => e.preventDefault()}
    >
      <FileUploadInput
        ref={inputRef}
        type="file"
        id="input-file-upload"
        data-testid="input-file-upload"
        onChange={trade?.isEtrTrade ? handleEtrDrop : handleDrop}
      />
      <FileUploadInputLabel
        id="label-file-upload"
        data-testid="label-file-upload"
        htmlFor="input-file-upload"
        variant={dragActive ? 'drag-active' : ''}
      >
        <div>
          {submitting ? (
            <ScaleLoader />
          ) : (
            <>
              <FileUploadInputLabelSpan>
                <FormattedMessage id="paymentsOnHold.uploadDocument.dragAndDrop" />{' '}
                <FileUploadBrowse>
                  <FormattedMessage id="paymentsOnHold.uploadDocument.browse" />
                </FileUploadBrowse>
              </FileUploadInputLabelSpan>
              <MaxFileSizeLabel>
                <FormattedMessage id="paymentsOnHold.uploadDocument.maxFileSize" />
                <br />
                {documentType === 0 ? (
                  <FormattedMessage id="paymentsOnHold.uploadDocument.title.allowedFileTypes" />
                ) : (
                  <FormattedMessage id="paymentsOnHold.uploadDocument.lien.allowedFileTypes" />
                )}
              </MaxFileSizeLabel>
            </>
          )}
        </div>
      </FileUploadInputLabel>
      {dragActive && (
        <FileUploadDragArea
          id="drag-file-element"
          onDrag={handleDrag}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={trade?.isEtrTrade ? handleEtrDrop : handleDrop}
        />
      )}
      <FilePreview
        uploadedDocuments={uploadedDocuments}
        uploading={uploading}
        documentType={documentType}
        removeDocument={removeDocument}
        removeEtrDocument={removeEtrDocument}
        isEtrTrade={trade?.isEtrTrade}
        previouslyUploadedEtrDocs={previouslyUploadedEtrDocs}
      />
      <StyledCancelButton onClick={onClose}>
        <FormattedMessage
          id="modal.cancel"
          data-testid="payments-on-hold-list-documents-modal-cancel-button"
        />
      </StyledCancelButton>
      <StyledSubmitDocumentsButton
        data-testid="submit-file-upload"
        disabled={uploading || submitting || uploadedDocuments.length === 0}
        onClick={
          trade?.isEtrTrade
            ? handleEtrSubmitDocumentsClick
            : handleSubmitDocumentsClick
        }
      >
        <FormattedMessage id="modal.submit" />
      </StyledSubmitDocumentsButton>
    </FileUploadForm>
  );
};

export default FileUpload;
